import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type CreateDomainAccessPayload = {
  domainId: number;
  userId: number;
  roleId: number;
};

export type DeleteDomainAccessPayload = {
  domainId: number;
  accessId: number;
};

export type DomainAccessEntry = {
  id: number;
  domain_id: number;
  user_id: number;
  user_email: string;
  user_full_name: string | null;
  role_id: number;
  role_name: string;
  created_at: string;
};

export type DomainAccessState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  entriesByDomainId: Record<number, DomainAccessEntry[]>;
};

export const domainAccessInitialState: DomainAccessState = {
  status: STATUS.IDLE,
  error: null,
  entriesByDomainId: {},
};

function isDomainAccessEntry(value: unknown): value is DomainAccessEntry {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.domain_id === "number" &&
    typeof row.user_id === "number" &&
    typeof row.user_email === "string" &&
    (row.user_full_name === null || typeof row.user_full_name === "string") &&
    typeof row.role_id === "number" &&
    typeof row.role_name === "string" &&
    typeof row.created_at === "string"
  );
}

function parseDomainAccessListResponse(data: unknown): DomainAccessEntry[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isDomainAccessEntry)) {
    return null;
  }
  return data;
}

export const fetchDomainAccesses = createAsyncThunk<
  { domainId: number; entries: DomainAccessEntry[] },
  number,
  { rejectValue: string }
>("domainAccess/fetch", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/domains/${domainId}/access`, { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const entries = parseDomainAccessListResponse(data);
    if (!entries) {
      return rejectWithValue("Unexpected response shape");
    }
    return { domainId, entries };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const createDomainAccess = createAsyncThunk<
  DomainAccessEntry,
  CreateDomainAccessPayload,
  { rejectValue: string }
>("domainAccess/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/domains/${payload.domainId}/access`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: payload.userId, role_id: payload.roleId }),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isDomainAccessEntry(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const deleteDomainAccess = createAsyncThunk<
  DeleteDomainAccessPayload,
  DeleteDomainAccessPayload,
  { rejectValue: string }
>("domainAccess/delete", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/domains/${payload.domainId}/access/${payload.accessId}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return payload;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const domainAccessSlice = createSlice({
  name: "domainAccess",
  initialState: domainAccessInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchDomainAccesses.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchDomainAccesses.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.entriesByDomainId[action.payload.domainId] = action.payload.entries;
      })
      .addCase(fetchDomainAccesses.rejected, (state) => {
        state.status = STATUS.SUCCEEDED;
        state.error = null;
      })
      .addCase(deleteDomainAccess.fulfilled, (state, action) => {
        const { domainId, accessId } = action.payload;
        const entries = state.entriesByDomainId[domainId];
        if (!entries) {
          return;
        }
        state.entriesByDomainId[domainId] = entries.filter((entry) => entry.id !== accessId);
      });
  },
});

export const domainAccessReducer = domainAccessSlice.reducer;

export function selectDomainAccessesForDomain(domainId: number | null) {
  return (state: RootState): DomainAccessEntry[] =>
    domainId === null ? [] : (state.domainAccess.entriesByDomainId[domainId] ?? []);
}

export const selectDomainAccessStatus = (state: RootState) => state.domainAccess.status;
